<template>
	<q-modal
		v-model="show_preview"
		:content-css="{ maxWidth: getMaxWidth, minWidth: getMinWidth }"
		no-backdrop-dismiss
		no-esc-dismiss
	>
		<q-modal-layout>
			<q-toolbar slot="header">
				<q-btn flat round dense @click="show_preview = false" icon="fas fa-times" />
				<q-toolbar-title>
					{{ $t(game_name) }}
				</q-toolbar-title>
			</q-toolbar>

			<div class="layout-padding">
				<div class="row q-pa-xs">
					<p class="col-12 text-bold">{{ $t('game_preview') }}</p>
					<div v-if="!game_finished" class="row">
						<p class="col-5 text-bold text-center">{{ $t('this') }}</p>
						<div class="col-2"></div>
						<p class="col-5 text-bold text-center">{{ $t('that') }}</p>
						<div
							v-for="(option, index) in options"
							:key="`o-${index}`"
							class="row q-ma-sm full-width flex"
						>
							<q-card
								class="q-pa-sm justify-center col-5 cursor-pointer"
								:class="getThisStyle(option)"
								@click.native="setAnswer(option, 1)"
							>
								<q-card-main class="text-center">
									{{ option.this.text }}
								</q-card-main>
							</q-card>
							<q-card class="q-pa-sm justify-center col-2" flat>
								<q-card-title class="text-center">
									{{ option.question }}
								</q-card-title>
							</q-card>
							<q-card
								class="q-pa-sm justify-center col-5 cursor-pointer"
								:class="getThatStyle(option)"
								@click.native="setAnswer(option, 2)"
							>
								<q-card-main class="text-center">
									{{ option.that.text }}
								</q-card-main>
							</q-card>
						</div>
					</div>

					<div class="col-10 q-mt-lg" v-if="game_finished">
						<p class="text-bold q-mt-sm">{{ $t('result') }}</p>

						<q-slider
							disable
							markers
							snap
							v-model="user_score"
							:color="game_finished ? getSliderColor() : 'blue-6'"
							:min="0"
							:max="number_of_choices"
						/>

						<p class="q-mt-sm">
							{{ `${$t('correct_answers')}: ${user_score} / ${number_of_choices}` }}
						</p>
						<p v-if="user_won">{{ $t(success_message) }}</p>
						<p v-else>{{ $t(failure_message) }}</p>
					</div>
				</div>

				<div class="col-xs-12 q-mt-md">
					<div class="row justify-end">
						<q-btn
							color="secondary"
							:label="$t('back')"
							@click="show_preview = false"
						/>
						<q-btn
							v-if="game_finished"
							color="primary"
							class="q-ml-md"
							:label="$t('play_again')"
							@click="resetGame"
						/>
					</div>
				</div>
			</div>
		</q-modal-layout>
	</q-modal>
</template>

<script>
export default {
	name: 'AdminGameThisOrThatPreview',

	props: ['show_game_preview', 'game_name', 'game_options'],

	data() {
		return {
			options: [],
			game_finished: false,
			qty_selected: 0,
			number_of_choices: 0,
			correct_choices: 0,
			user_score: 0,
			user_won: false,
			success_message: `${this.game_name}_success_message`,
			failure_message: `${this.game_name}_failure_message`,
		}
	},

	computed: {
		show_preview: {
			get() {
				return this.show_game_preview
			},
			set() {
				this.$emit('hide_preview', '')
			},
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '90vw'
			else return '75vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '50vw'
		},
	},

	methods: {
		getThisStyle(question) {
			if (question.selected === 1 && question.correct === undefined) return 'selected'
			if (question.correct === true && question.correct_choice === 1) return 'correct'
			if (question.correct === false && question.correct_choice === 1)
				return 'incorrect shake'
			else return 'invisible-border'
		},

		getThatStyle(question) {
			if (question.selected === 2 && question.correct === undefined) return 'selected'
			if (question.correct === true && question.correct_choice === 2) return 'correct'
			if (question.correct === false && question.correct_choice === 2)
				return 'incorrect shake'
			else return 'invisible-border'
		},

		getSliderColor() {
			let medium = Math.ceil(this.number_of_choices / 2)

			if (this.correct_choices < medium) return 'red-6'
			else if (this.correct_choices == medium) return 'blue-6'
			else return 'green-6'
		},

		setAnswer(option, answer) {
			const userAnswer = this.options.find((o) => o.question === option.question)
			if (userAnswer.selected === answer) {
				this.qty_selected -= 1
				return (userAnswer.selected = null)
			}

			this.qty_selected += 1
			userAnswer.selected = answer

			if (this.qty_selected >= this.options.length - 1) this.checkResults()
		},

		checkResults() {
			if (this.options.filter((o) => o.selected === null).length) return

			setTimeout(() => {
				this.options = this.options.map((o) => ({
					...o,
					correct: o.selected === o.correct_choice,
				}))
				this.correct_choices = this.options.filter(
					(o) => o.selected === o.correct_choice
				).length
				setTimeout(() => {
					this.game_finished = true
				}, 1750)
				setTimeout(() => {
					this.user_score = this.correct_choices
					this.user_won = this.correct_choices >= this.options.length
				}, 2000)
			}, 250)
		},

		resetGame() {
			this.buildGame()

			this.correct_choices = 0
			this.qty_selected = 0
			this.user_score = 0
			this.user_won = false

			this.game_finished = false
		},

		buildGame() {
			this.options = this.game_options
				.map((o) => ({
					...o,
					correct: undefined,
					selected: null,
					position: Math.floor(Math.random() * 100),
				}))
				.sort((a, b) => b.position - a.position)

			this.number_of_choices = this.game_options.length
		},
	},

	mounted() {
		this.buildGame()
	},
}
</script>

<style lang="stylus">
.invisible-border
  border 2px solid transparent
  transition all 0.75s ease

.selected
  transition all 0.75s ease
  border 2px solid #386cff
  background #93cdff

.correct
  background #80db69
  border 2px solid #35ad35
  transition all 0.75s ease

.incorrect
  transition all 0.75s ease
  background #ff6666
  border 2px solid #d93333

.shake
  animation shake 1s

@keyframes shake {
  0% { transform: translate(2px, 2px) rotate(0deg); }
  10% { transform: translate(-1px, -2px) rotate(-1deg); }
  20% { transform: translate(-4px, 0px) rotate(1deg); }
  30% { transform: translate(4px, 3px) rotate(0deg); }
  40% { transform: translate(2px, -2px) rotate(1deg); }
  50% { transform: translate(-2px, 3px) rotate(-1deg); }
  60% { transform: translate(-4px, 2px) rotate(0deg); }
  70% { transform: translate(4px, 2px) rotate(-1deg); }
  80% { transform: translate(-1px, -1px) rotate(1deg); }
  90% { transform: translate(1px, 2px) rotate(0deg); }
  100% { transform: translate(1px, -2px) rotate(-1deg); }
}
</style>
